/**
 * Color tokens para charts SVG inline.
 *
 * SINGLE SOURCE OF TRUTH para los hex values que aparecen en fill/stroke de
 * SVGs. Antes cada chart tenía su propio objeto `COLORS = {...}` con hex
 * hardcoded; algunos usaban #10b981 (emerald-500), otros el #84cc16 (lime-500)
 * del tailwind config — drift real entre el design system y los charts.
 *
 * Estos tokens matchean exactamente `tailwind.config.js → theme.extend.colors.eve.*`.
 * Si el design system cambia, actualizá también acá (o idealmente migrá a
 * CSS custom properties para que se actualice solo).
 *
 * Por qué hex en lugar de classes Tailwind: `fill-[#xxx]` y `stroke-[#xxx]`
 * funcionan en SVG attributes pero los JIT class names son frágiles.
 * `fill="var(--color-eve-accent)"` no funciona en atributos SVG nativos
 * (Chrome sí lo acepta, Safari no consistentemente). Hex es lo más portable.
 *
 * Convención de uso:
 *   - status colors (success/danger/warn) → SOLO para encoding de status
 *     (margin sign, price trend). NUNCA para "serie 4" en un chart multi-serie.
 *   - accent → primary highlight (linea activa, dot del "ahora")
 *   - muted → eje labels, ticks, baseline
 *   - border → gridlines
 */

export const CHART_COLORS = {
  // Status — reservados para encoding de polarity
  success: '#84cc16',  // lime-500 = eve-success
  danger: '#dc2626',   // red-600 = eve-danger
  warn: '#facc15',     // yellow-400 = eve-warn
  // Identity
  accent: '#f59e0b',   // amber-500 = eve-accent
  accent2: '#ea580c',  // orange-600 = eve-accent2
  // Neutrals
  muted: '#a8a29e',    // stone-400 = eve-muted
  text: '#fafaf9',     // stone-50 = eve-text
  border: '#44403c',   // stone-700 = eve-border
  panel: '#292524',    // stone-800 = eve-panel
  bg: '#1c1917',       // stone-900 = eve-bg
  // Semánticos para charts
  baseline: '#44403c', // alias of border — Y=0 line
  flat: '#a8a29e',     // alias of muted — trend flat / no change
} as const

/**
 * Trend color helper — dado un primer y último valor, devuelve el color
 * según el porcentaje de cambio. ±0.1% se considera "flat" (gris).
 *
 * Usado por WalletSparkline, MarginHistory, etc.
 */
export function trendColor(
  first: number,
  last: number,
): typeof CHART_COLORS.success | typeof CHART_COLORS.danger | typeof CHART_COLORS.flat {
  if (first <= 0) return CHART_COLORS.flat
  const deltaPct = ((last - first) / first) * 100
  if (Math.abs(deltaPct) < 0.1) return CHART_COLORS.flat
  return deltaPct > 0 ? CHART_COLORS.success : CHART_COLORS.danger
}
